'use client';

import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const EFFECTIVE_DATE = 'May 7, 2026';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <Navigation />

      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Privacy{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="text-gray-400">Effective {EFFECTIVE_DATE}</p>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-gray-800/40 border border-gray-700 rounded-xl p-8 md:p-12 space-y-10 text-gray-300 leading-relaxed">

          <div>
            <p>
              Reclipt ("Reclipt", "we", "us") provides AI-powered tools for
              uploading, transcribing, searching, and clipping spoken-word
              video content. This Privacy Policy describes the information we
              collect when you use the Reclipt website, dashboard, and related
              services (the "Service"), how we use it, and the choices you
              have. By using the Service you agree to this Policy.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">1. Information we collect</h2>
            <h3 className="text-lg font-semibold text-white mt-6 mb-2">Account information</h3>
            <p>
              When you create a Reclipt account we collect your name, email
              address, and authentication identifiers from our sign-in
              provider (Clerk). If you sign in with Google, we receive your
              name, email address, profile picture, and a unique Google
              account identifier from the <code className="text-blue-300">openid</code>,{' '}
              <code className="text-blue-300">email</code>, and{' '}
              <code className="text-blue-300">profile</code> scopes.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-2">Video content you upload</h3>
            <p>
              Videos and audio files you upload, paste links to, or import
              from a connected source, along with the transcripts, captions,
              clips, tags, and metadata that Reclipt generates from them.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-2">Google Drive content (optional)</h3>
            <p>
              If you choose to import a video from Google Drive using the
              built-in picker, Reclipt requests the{' '}
              <code className="text-blue-300">https://www.googleapis.com/auth/drive.readonly</code>{' '}
              scope. We use this scope solely to download the specific file
              you select in the picker so we can process it inside Reclipt.
              We do not browse, scan, or access any other files in your
              Drive, and we do not request or use this scope for advertising,
              training generalized AI models, or any unrelated purpose.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-2">Usage and device data</h3>
            <p>
              We collect basic usage information such as pages viewed,
              actions taken in the dashboard, browser type, IP address, and
              error logs. This is used to operate, secure, and improve the
              Service.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-2">Payment information</h3>
            <p>
              Subscription payments are processed by our payment provider.
              Reclipt does not store full credit card numbers on our servers;
              we only retain a limited record (such as the last four digits
              and billing status) to manage your subscription.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">2. How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide the Service: transcription, search, clipping, exporting, and storage of your video content.</li>
              <li>To authenticate your account and keep it secure.</li>
              <li>To respond to support requests and communicate operational changes.</li>
              <li>To monitor performance, debug errors, and prevent abuse.</li>
              <li>To process payments and manage subscriptions.</li>
            </ul>
            <p className="mt-4">
              We do <strong>not</strong> sell your personal information, and
              we do <strong>not</strong> use the contents of your videos or
              your Google user data to serve advertising.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              3. Google API Services User Data Policy
            </h2>
            <p>
              Reclipt's use and transfer to any other app of information
              received from Google APIs will adhere to the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. Specifically:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>We only use Google user data to provide or improve user-facing features that are prominent in the Reclipt application.</li>
              <li>We do not transfer Google user data to third parties except as necessary to provide or improve those features, to comply with applicable law, or as part of a merger, acquisition, or sale of assets with notice to users.</li>
              <li>We do not use Google user data for serving advertisements.</li>
              <li>We do not allow humans to read your Google user data unless we have your affirmative consent for specific files, it is necessary for security purposes (such as investigating abuse), to comply with applicable law, or for internal operations and only when the data has been aggregated and anonymized.</li>
              <li>We do not use Google user data to develop, improve, or train generalized or non-personalized AI or machine learning models.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">4. How we store and protect data</h2>
            <p>
              Account and metadata are stored in our managed Postgres
              database (Supabase). Uploaded videos and generated clips are
              stored in object storage (Wasabi S3) under access controls
              scoped to your account. Transcripts and search indexes are
              processed and held by Reclipt and our infrastructure providers
              (including Railway and Coconut.co) solely to operate the
              Service.
            </p>
            <p className="mt-4">
              We use TLS for data in transit and access controls,
              authentication, and least-privilege practices for data at rest.
              No system is perfectly secure, but we continually work to
              protect your data against unauthorized access.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data sharing</h2>
            <p>We share information only as follows:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Service providers</strong> who process data on our behalf to operate Reclipt (authentication, hosting, storage, transcription, video processing, payments, analytics, customer support).</li>
              <li><strong>Compliance</strong> when required by law, legal process, or to protect the rights, property, or safety of Reclipt, our users, or others.</li>
              <li><strong>Business transfers</strong> in connection with a merger, acquisition, financing, or sale of assets, with notice to affected users.</li>
              <li><strong>With your direction</strong>, such as when you choose to export, share, or publish a clip.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data retention and deletion</h2>
            <p>
              We retain your account information and uploaded content for as
              long as your account is active. You can delete individual
              videos and clips at any time from the dashboard. To delete your
              entire account and associated data, contact us at{' '}
              <a href="mailto:support@reclipt.io" className="text-blue-400 hover:text-blue-300 underline">
                support@reclipt.io
              </a>
              . We will delete or anonymize your data within 30 days, except
              where we are required to retain it for legal, tax, or
              fraud-prevention reasons.
            </p>
            <p className="mt-4">
              You can revoke Reclipt's access to your Google account at any
              time at{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                myaccount.google.com/permissions
              </a>
              .
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">7. Your rights</h2>
            <p>
              Depending on your location, you may have rights to access,
              correct, export, or delete the personal information we hold
              about you, and to object to or restrict certain processing. To
              exercise these rights, contact us at{' '}
              <a href="mailto:support@reclipt.io" className="text-blue-400 hover:text-blue-300 underline">
                support@reclipt.io
              </a>
              .
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">8. Children</h2>
            <p>
              Reclipt is not directed to children under 13 (or under 16 in
              the European Economic Area), and we do not knowingly collect
              personal information from them. If you believe a child has
              provided us with personal information, please contact us so we
              can delete it.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">9. International users</h2>
            <p>
              Reclipt operates globally. By using the Service you consent to
              the transfer of your information to countries that may have
              different data-protection laws than your own.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">10. Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time. Material changes
              will be announced through the Service or by email. Continued
              use of Reclipt after a change becomes effective constitutes
              acceptance of the updated Policy.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">11. Contact us</h2>
            <p>
              Questions or requests about this Policy can be sent to{' '}
              <a href="mailto:support@reclipt.io" className="text-blue-400 hover:text-blue-300 underline">
                support@reclipt.io
              </a>
              .
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
